import { createColumnHelper } from '@tanstack/react-table';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Menu,
  Search,
  UserCheck,
  UserX,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { downloadCheckInReport } from '~entities/recital-manager/api/export-check-in-report';
import { useGetAllShows } from '~entities/recital-manager/api/get-all-shows';
import {
  useCheckIns,
  useCheckInStats,
  useCheckOut,
  useCreateCheckIn,
  useStudentsAvailableForCheckIn,
} from '~entities/recital-manager/api/hooks/useCheckIns';
import type { RecitalCheckIn } from '~entities/recital-manager/model/types/volunteers';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { EnrollioTable } from '~/shared/components/enrollio-table';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import { Input } from '~/shared/shadcn/ui/input';
import { Label } from '~/shared/shadcn/ui/label';
import { Progress } from '~/shared/shadcn/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '~/shared/shadcn/ui/sheet';
import { formatRecitalTime } from '~shared/utils/recital-timezone';

import {
  calculateCheckInDuration,
  filterCheckInsByName,
  getActiveHistoryFilterCount,
  resolveCheckInApiTypeFilter,
} from './check-in-tab.helpers';
import {
  CHECK_IN_HISTORY_TYPE_FILTER,
  type CheckInHistoryTypeFilter,
} from './check-in-tab.types';

interface CheckInTabProps {
  recitalId: string;
}
const columnHelper = createColumnHelper<RecitalCheckIn>();

export const CheckInTab = ({ recitalId }: CheckInTabProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<CheckInHistoryTypeFilter>(
    CHECK_IN_HISTORY_TYPE_FILTER.ALL,
  );
  const [showFilter, setShowFilter] = useState<string | undefined>(undefined);
  const [checkInHistoryActionsOpen, setCheckInHistoryActionsOpen] =
    useState(false);

  const activeHistoryFilterCount = getActiveHistoryFilterCount(
    typeFilter,
    showFilter,
  );
  const apiTypeFilter = resolveCheckInApiTypeFilter(typeFilter);

  // Get studio timezone
  const studio = useCurrentStudio();
  const timezone = studio?.timezone ?? 'UTC';

  const { data: showsResponse } = useGetAllShows({ recitalId });
  const shows = showsResponse?.data ?? [];
  const { data: checkIns = [], isLoading: checkInsLoading } = useCheckIns(
    recitalId,
    {
      type: apiTypeFilter,
      showId: showFilter,
    },
  );
  const { data: stats } = useCheckInStats(recitalId, showFilter);
  const { data: availableStudents = [], isLoading: studentsLoading } =
    useStudentsAvailableForCheckIn(recitalId, showFilter);
  const createCheckInMutation = useCreateCheckIn();
  const checkOutMutation = useCheckOut();
  const { hasAdminAccess } = useCurrentUserRole();

  const filteredCheckIns = useMemo(
    () => filterCheckInsByName(checkIns, searchQuery),
    [checkIns, searchQuery],
  );

  const studentCheckInRate = stats?.totalStudents
    ? (stats.checkedInStudents / stats.totalStudents) * 100
    : 0;

  const volunteerCheckInRate = stats?.totalVolunteers
    ? (stats.checkedInVolunteers / stats.totalVolunteers) * 100
    : 0;

  const handleExportReport = async (format: 'csv' | 'pdf') => {
    try {
      await downloadCheckInReport(recitalId, format, {
        showId: showFilter,
        type: apiTypeFilter,
      });
      toast.success(
        format === 'csv'
          ? 'Your check-in list downloaded as a spreadsheet.'
          : 'Your check-in list downloaded as a PDF.',
      );
    } catch (error) {
      console.error('Failed to export check-in report', {
        recitalId,
        format,
        error,
      });
      toast.error(
        'We could not download the check-in list. Please try again.',
      );
    }
  };

  const handleCheckInStudent = async (
    studentId: string,
    studentName: string,
  ) => {
    try {
      await createCheckInMutation.mutateAsync({
        recitalId,
        data: {
          type: 'student',
          studentId,
          method: 'manual',
        },
      });
      toast.success(`${studentName} checked in successfully`);
    } catch (error) {
      console.error('Failed to check in student', { recitalId, studentId, error });
      toast.error('We could not check in this student. Please try again.');
    }
  };

  const handleCheckOutStudent = async (
    studentId: string,
    studentName: string,
  ) => {
    try {
      await checkOutMutation.mutateAsync({
        recitalId,
        data: {
          type: 'student',
          studentId,
          method: 'manual',
        },
      });
      toast.success(`${studentName} checked out successfully`);
    } catch (error) {
      console.error('Failed to check out student', { recitalId, studentId, error });
      toast.error('We could not check out this student. Please try again.');
    }
  };
  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (checkIn) => checkIn.studentName ?? checkIn.parentName ?? 'Unknown',
        { id: 'name', header: 'Name' },
      ),
      columnHelper.accessor('checkedInAt', {
        header: 'Checked in at',
        cell: (info) =>
          formatRecitalTime(info.getValue(), timezone, 'MMM d, h:mm a'),
      }),
      columnHelper.accessor('checkedOutAt', {
        header: 'Checked out at',
        cell: (info) => {
          const checkedOutAt = info.getValue();
          return checkedOutAt
            ? formatRecitalTime(checkedOutAt, timezone, 'MMM d, h:mm a')
            : '—';
        },
      }),
      columnHelper.display({
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) =>
          row.original.checkedOutAt ? (
            <Badge variant='outline'>
              {calculateCheckInDuration(
                row.original.checkedInAt,
                row.original.checkedOutAt,
              )}
            </Badge>
          ) : (
            '—'
          ),
      }),
    ],
    [timezone],
  );

  const renderCheckInHistoryFilters = (stacked: boolean) => (
    <>
      <div className={stacked ? 'space-y-2' : undefined}>
        {stacked ? (
          <Label htmlFor='check-in-history-type-filter'>Type</Label>
        ) : null}
        <Select
          value={typeFilter}
          onValueChange={(value) => {
            setTypeFilter(value as CheckInHistoryTypeFilter);
          }}
        >
          <SelectTrigger
            id={stacked ? 'check-in-history-type-filter' : undefined}
            className={stacked ? 'w-full' : 'w-[180px]'}
          >
            <SelectValue placeholder='Filter by type' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CHECK_IN_HISTORY_TYPE_FILTER.ALL}>
              All Types
            </SelectItem>
            <SelectItem value={CHECK_IN_HISTORY_TYPE_FILTER.STUDENT}>
              Students
            </SelectItem>
            <SelectItem value={CHECK_IN_HISTORY_TYPE_FILTER.VOLUNTEER}>
              Volunteers
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      {shows.length > 0 ? (
        <div className={stacked ? 'space-y-2' : undefined}>
          {stacked ? (
            <Label htmlFor='check-in-history-show-filter'>Show</Label>
          ) : null}
          <Select
            value={showFilter ?? 'all'}
            onValueChange={(value) => {
              setShowFilter(value === 'all' ? undefined : value);
            }}
          >
            <SelectTrigger
              id={stacked ? 'check-in-history-show-filter' : undefined}
              className={stacked ? 'w-full' : 'w-[200px]'}
            >
              <SelectValue placeholder='Filter by show' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Shows</SelectItem>
              {shows.map((show) => (
                <SelectItem key={show._id} value={show._id}>
                  {show.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
    </>
  );

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-2xl font-semibold tracking-tight'>
          Check-In Management
        </h2>
        <p className='text-sm text-muted-foreground'>
          Track student and volunteer attendance for your recital
        </p>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium'>
              Total Check-Ins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{checkIns.length}</div>
            <p className='mt-1 text-xs text-muted-foreground'>
              All manual check-ins
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium'>
              Student Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {stats?.checkedInStudents ?? 0} / {stats?.totalStudents ?? 0}
            </div>
            <Progress value={studentCheckInRate} className='mt-2' />
            <p className='mt-1 text-xs text-muted-foreground'>
              {studentCheckInRate.toFixed(0)}% Attendance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className='text-sm font-medium'>
              Volunteer Attendance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {stats?.checkedInVolunteers ?? 0} / {stats?.totalVolunteers ?? 0}
            </div>
            <Progress value={volunteerCheckInRate} className='mt-2' />
            <p className='mt-1 text-xs text-muted-foreground'>
              {volunteerCheckInRate.toFixed(0)}% Present
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Available Students for Check-In */}
      <Card>
        <CardHeader>
          <CardTitle>Students Available for Check-In</CardTitle>
          <CardDescription>
            Students who are scheduled to perform but haven&apos;t checked in
            yet
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          {studentsLoading ? (
            <div className='py-8 text-center text-muted-foreground'>
              Loading available students...
            </div>
          ) : availableStudents.length === 0 ? (
            <div className='py-8 text-center text-muted-foreground'>
              {showFilter
                ? 'No students available for this show'
                : 'No students available for check-in'}
            </div>
          ) : (
            <div className='space-y-2'>
              {availableStudents.map((student) => (
                <div
                  key={student.studentId}
                  className='flex flex-col gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/50 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='flex-1'>
                    <div className='flex items-center gap-3'>
                      <div className='flex items-center gap-2'>
                        {student.isCheckedIn ? (
                          <UserCheck className='size-4 text-green-600' />
                        ) : (
                          <UserX className='size-4 text-gray-400' />
                        )}
                        <span className='font-medium capitalize'>
                          {student.firstName} {student.lastName}
                        </span>
                      </div>
                      {student.isCheckedIn && (
                        <Badge
                          variant={
                            student.canCheckOut ? 'default' : 'secondary'
                          }
                          className='text-xs'
                        >
                          Checked In
                          {student.checkedInAt &&
                            ` at ${formatRecitalTime(student.checkedInAt, timezone)}`}
                        </Badge>
                      )}
                    </div>
                    <div className='mt-1 text-sm text-muted-foreground'>
                      {student.performances.length > 0 && (
                        <span>
                          Performing in:{' '}
                          <span className='capitalize'>
                            {student.performances
                              .map((p) => p.performanceName)
                              .join(', ')}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                  {!student.isCheckedIn ? (
                    <>
                      {hasAdminAccess && (
                        <Button
                          size='sm'
                          onClick={() =>
                            handleCheckInStudent(
                              student.studentId,
                              `${student.firstName} ${student.lastName}`,
                            )
                          }
                          disabled={createCheckInMutation.isPending}
                        >
                          Check In
                        </Button>
                      )}
                    </>
                  ) : student.canCheckOut ? (
                    <>
                      {hasAdminAccess && (
                        <Button
                          size='sm'
                          variant='secondary'
                          onClick={() =>
                            handleCheckOutStudent(
                              student.studentId,
                              `${student.firstName} ${student.lastName}`,
                            )
                          }
                          disabled={checkOutMutation.isPending}
                        >
                          Check Out
                        </Button>
                      )}
                    </>
                  ) : (
                    <Badge variant='secondary' className='text-xs'>
                      Checked In
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Check-Ins List */}
      <Card>
        <CardHeader>
          <CardTitle className='flex flex-wrap items-center gap-2'>
            Check-In History
            <Badge variant='secondary'>
              {filteredCheckIns.length.toLocaleString()}
            </Badge>
          </CardTitle>
          <CardDescription>
            Live list of check-ins for this recital. Search only filters what
            you see here; exports use your type and show filters.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnrollioTable
            showTotal={false}
            totalLabel='Total'
            filtersLayout='inline'
            filterHeader={
              <>
                <Sheet
                  open={checkInHistoryActionsOpen}
                  onOpenChange={setCheckInHistoryActionsOpen}
                >
                  <SheetTrigger asChild>
                    <Button
                      variant='outline'
                      className='relative w-full md:hidden'
                    >
                      <Menu className='mr-2 size-4' />
                      Check-in actions
                      {activeHistoryFilterCount > 0 ? (
                        <Badge className='ml-2' variant='secondary'>
                          {activeHistoryFilterCount}
                        </Badge>
                      ) : null}
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side='right'
                    className='w-[300px] overflow-y-auto'
                  >
                    <SheetHeader>
                      <SheetTitle>Check-in actions</SheetTitle>
                      <SheetDescription>
                        Download the list or filter by type and show. Exports
                        match those filters, not the search box.
                      </SheetDescription>
                    </SheetHeader>
                    <div className='mt-6 flex flex-col gap-3'>
                      <Button
                        variant='outline'
                        className='w-full justify-start'
                        size='sm'
                        onClick={() => {
                          setCheckInHistoryActionsOpen(false);
                          void handleExportReport('csv');
                        }}
                      >
                        <FileSpreadsheet className='mr-2 size-4' />
                        Export as CSV
                      </Button>
                      <Button
                        variant='outline'
                        className='w-full justify-start'
                        size='sm'
                        onClick={() => {
                          setCheckInHistoryActionsOpen(false);
                          void handleExportReport('pdf');
                        }}
                      >
                        <FileText className='mr-2 size-4' />
                        Export as PDF
                      </Button>
                      <div className='border-t pt-3' />
                      {renderCheckInHistoryFilters(true)}
                    </div>
                  </SheetContent>
                </Sheet>
                <div className='relative w-full min-w-0 flex-1 md:min-w-56'>
                  <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                  <Input
                    placeholder='Search by name...'
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                    }}
                    className='pl-9'
                  />
                </div>
                <div className='hidden flex-wrap items-center gap-2 md:flex'>
                  {renderCheckInHistoryFilters(false)}
                </div>
              </>
            }
            toolbar={
              <div className='hidden md:block'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='outline' size='sm' className='gap-2'>
                      <Download className='size-4' />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      onClick={() => {
                        void handleExportReport('csv');
                      }}
                    >
                      <FileSpreadsheet className='mr-2 size-4' />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        void handleExportReport('pdf');
                      }}
                    >
                      <FileText className='mr-2 size-4' />
                      Export as PDF
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            }
            data={filteredCheckIns}
            columns={columns}
            getRowId={(row) => row._id}
            isUpdating={checkInsLoading}
            emptyContent='No check-ins yet.'
            ariaLabel='Recital check-in history'
          />
        </CardContent>
      </Card>
    </div>
  );
};
